import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useAccount } from '@/api/useAccount';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { formatDate, formatInstantWithZone, usableTimeZone, zoneName } from '@/lib/format';
import { parseIsoDate, parseWallClock, todayInZone, zonedTimeToInstant } from '@/lib/timezone';
import type { components } from '@/api/generated/schema';
import { LeadSourceField } from '@/components/customers/LeadSourceField';
import { PropertyLocationMap } from '@/components/properties/PropertyLocationMap';
import { useTechnicians } from './dispatch/useDispatchBoard';
import { NotFound } from './NotFound';
import { toNumericCoordinate, useProperty } from './properties/useProperty';
import {
  JobNotFoundError,
  JobNotPermittedError,
  useAddJobNote,
  useAssignJobFromDetail,
  useChangeJobStatus,
  useEditJobNote,
  useJob,
  useSetJobLeadSource,
  JobStatusRefusedError,
  type JobNote,
} from './jobs/useJobDetail';
import { useJobPhotos, useUploadJobPhotos, type UploadItem } from './jobs/useJobPhotos';
import { JobForms } from './jobs/JobForms';
import { JobVisits } from '@/components/jobs/JobVisits';
import { JobEstimates } from '@/components/jobs/JobEstimates';
import { JobWork } from '@/components/jobs/JobWork';
import { JobInvoice } from '@/components/jobs/JobInvoice';
import { JobTime } from '@/components/jobs/JobTime';

type JobStatus = Exclude<components['schemas']['JobStatus'], null>;
type PropertyParties = NonNullable<components['schemas']['PropertyResponse']['parties']>;

/**
 * The PRIMARY party in this role, or the first if none is marked primary —
 * mirrors PropertyPartiesSection's own "one primary per role" framing. Used
 * for the read-only owner/manager line (M1-25); undefined when nobody holds
 * that role yet, which the caller reads as "don't show this line".
 */
function propertyParty(parties: PropertyParties | undefined, role: string): string | undefined {
  const matches = (parties ?? []).filter((party) => party.role === role);
  return (matches.find((party) => party.isPrimary) ?? matches[0])?.displayName;
}

const fieldClass =
  'h-9 rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * One job: everything about it, and everything office staff or the assigned
 * technician can do to it from a browser (M1-13).
 *
 * <b>Reached from the job list (M1-15) and the dispatch board.</b> The board
 * card is itself the drag handle and the move dialog's trigger (M1-11) — a
 * second click target nested inside that button is invalid HTML and would
 * fight the drag gesture — so the link lives in `MoveJobDialog` instead,
 * which already opens on a card click and touches none of that.
 *
 * <b>A Technician holds `jobs.work` but not `jobs.read.all`</b> (M1-13's own
 * AC calls this out), and the API narrows them to jobs assigned to them. A
 * 403 here is that narrowing working, not a broken page: it renders a plain
 * "not yours" message, not the app-wide `Forbidden` route guard, because the
 * guard denies a whole SCREEN and this denies one RECORD. Since M1-22 they
 * also reach this page from the job list, which narrows itself the same way —
 * before that, a direct link somebody sent them was the only way in.
 */
export function JobDetail() {
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const hasPermission = useHasPermission();
  const canAssign = hasPermission(Permissions.assignJobs);
  // ⚠️ customers.manage, NOT assignJobs — matching the gate the route itself
  // carries. Where a job came from is an office fact about the customer
  // relationship, so a Dispatcher holds it and a Technician does not.
  const canManageCustomers = hasPermission(Permissions.manageCustomers);

  const account = useAccount();
  const timeZone = account.data?.timezone;
  const currentUser = useCurrentUser();
  const job = useJob(jobId);
  // Unconditional, ahead of job's own pending/error returns below (Rules of
  // Hooks) — resolves to `undefined` until the job loads, which is exactly
  // what useProperty's own `enabled` guard is for. Read-only mini-map (M1-26,
  // the AC's "property panel used from the Job screen"): this page has no
  // property-editing surface, so it never passes `editable`.
  const property = useProperty(job.data?.job.propertyId);
  // Gated on canAssign (M1-22): GET /users needs `users.read`, which a
  // Technician does not hold, so an unconditional call meant every job this
  // page opened on a phone started with a 403 nobody ever sees. The
  // assignment box that consumes it is already behind the same flag.
  const technicians = useTechnicians(canAssign);

  const changeStatus = useChangeJobStatus(jobId ?? '');
  const assign = useAssignJobFromDetail(jobId ?? '');
  const addNote = useAddJobNote(jobId ?? '');
  const setLeadSource = useSetJobLeadSource(jobId ?? '');
  const editNote = useEditJobNote(jobId ?? '');
  const photos = useJobPhotos(jobId);
  const upload = useUploadJobPhotos(jobId);

  const [announcement, setAnnouncement] = useState('');
  const [targetStatus, setTargetStatus] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleStart, setScheduleStart] = useState('09:00');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteCustomerVisible, setNoteCustomerVisible] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [editCustomerVisible, setEditCustomerVisible] = useState(false);

  // A stale or mistyped link is a missing page, not a failed request — same
  // reasoning CustomerDetail gives CustomerNotFoundError (M1-04).
  if (job.error instanceof JobNotFoundError) {
    return <NotFound />;
  }

  if (job.isPending) {
    return <LoadingState label={t('jobDetail.loading')} />;
  }

  // Not the route-level Forbidden screen: that denies a whole section of the
  // app, and a Technician is correctly denied only THIS job, not every job.
  if (job.error instanceof JobNotPermittedError) {
    return (
      <EmptyState
        title={t('jobDetail.notPermittedTitle')}
        description={t('jobDetail.notPermittedDescription')}
      />
    );
  }

  if (job.isError) {
    return (
      <ErrorState
        title={t('jobDetail.loadErrorTitle')}
        description={t('jobDetail.loadErrorDescription')}
        onRetry={() => void job.refetch()}
      />
    );
  }

  const detail = job.data;
  const record = detail.job;
  // M2-21: the clock the job's APPOINTMENTS are told on — the property's,
  // else the shop's (`appointmentTimezone`). The visits, the scheduled time
  // and their editors are on it, so the page says what the customer's emails
  // and the API's sentences ("…sent at 10:00 AM") say; each names its zone.
  // The technician's time (JobTime) and the work stay on the shop's clock.
  // An id this runtime cannot resolve falls back rather than crash the page.
  const appointmentZone = timeZone ? usableTimeZone(detail.appointmentTimezone, timeZone) : undefined;

  function submitStatusChange() {
    if (!targetStatus) {
      return;
    }
    const scheduling = targetStatus === 'Scheduled';
    let scheduledStart: string | null = null;
    let scheduledEnd: string | null = null;
    if (scheduling) {
      const date = parseIsoDate(scheduleDate) ?? (appointmentZone ? todayInZone(appointmentZone) : null);
      const startMinute = parseWallClock(scheduleStart);
      if (!date || startMinute === null || !appointmentZone) {
        return;
      }
      scheduledStart = zonedTimeToInstant(
        date,
        Math.floor(startMinute / 60),
        startMinute % 60,
        appointmentZone,
      ).toISOString();
      const endMinute = scheduleEnd ? parseWallClock(scheduleEnd) : null;
      if (endMinute !== null) {
        scheduledEnd = zonedTimeToInstant(
          date,
          Math.floor(endMinute / 60),
          endMinute % 60,
          appointmentZone,
        ).toISOString();
      }
    }

    changeStatus.mutate(
      { status: targetStatus as JobStatus, scheduledStart, scheduledEnd },
      {
        onSuccess: () => {
          setAnnouncement(
            t('jobDetail.status.changed', { status: t(`common.jobStatus.${targetStatus}`) }),
          );
          setTargetStatus('');
        },
        // The API's own sentence, not a generic failure: a 409 here says WHY —
        // an illegal transition, or (M1-20) the forms this job still needs
        // submitted, named. "Couldn't change the status" would throw away the
        // only part a technician can act on.
        onError: (failure) =>
          setAnnouncement(
            failure instanceof JobStatusRefusedError
              ? t('jobDetail.status.refused', { reason: failure.message })
              : t('jobDetail.status.failed'),
          ),
      },
    );
  }

  function submitAssign() {
    assign.mutate(assignTo || null, {
      onSuccess: () => {
        const name = technicians.data?.find((technician) => technician.id === assignTo)?.name;
        setAnnouncement(
          name ? t('jobDetail.assign.assigned', { name }) : t('jobDetail.assign.unassigned'),
        );
      },
      onError: () => setAnnouncement(t('jobDetail.assign.failed')),
    });
  }

  function submitNote() {
    if (!noteBody.trim()) {
      return;
    }
    addNote.mutate(
      { body: noteBody.trim(), customerVisible: noteCustomerVisible },
      {
        onSuccess: () => {
          setNoteBody('');
          setNoteCustomerVisible(false);
          setAnnouncement(t('jobDetail.notes.added'));
        },
        onError: () => setAnnouncement(t('jobDetail.notes.addFailed')),
      },
    );
  }

  function startEditingNote(note: JobNote) {
    setEditingNoteId(note.id);
    setEditBody(note.body);
    setEditCustomerVisible(note.customerVisible);
  }

  function submitNoteEdit(noteId: string) {
    if (!editBody.trim()) {
      return;
    }
    editNote.mutate(
      { noteId, edits: { body: editBody.trim(), customerVisible: editCustomerVisible } },
      {
        onSuccess: () => {
          setEditingNoteId(null);
          setAnnouncement(t('jobDetail.notes.edited'));
        },
        onError: () => setAnnouncement(t('jobDetail.notes.editFailed')),
      },
    );
  }

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        {/* Ungated since M1-22: the list is now reachable by everyone this
            route already lets in (`jobs.work`), narrowing itself server-side,
            so a Technician who arrived from it needs the way back. It used to
            be hidden from them precisely because there was nothing there. */}
        <p className="text-sm">
          <Link
            to="/jobs"
            className="tap-target text-[var(--color-muted-foreground)] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          >
            {t('jobDetail.backToJobs')}
          </Link>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight break-words">
            {record.customerName}
          </h1>
          <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium">
            {t(`common.jobStatus.${record.status}`, { defaultValue: record.status })}
          </span>
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)]">{record.description}</p>
      </div>

      {/* ---- Details --------------------------------------------------- */}
      <div className="rounded-lg border p-4">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobDetail.property')}
            </dt>
            <dd>
              {/* M1-05's detail page — gate code, access notes, past jobs —
                  reachable from here, including by a Technician who has no
                  other way to reach it (the API narrows them to properties
                  they have a job at, same shape as this page's own guard). */}
              <Link
                to={`/properties/${record.propertyId}`}
                className="underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
              >
                <address className="not-italic break-words">
                  {record.propertyLine1}
                  {record.propertyUnit ? `, ${record.propertyUnit}` : ''}
                </address>
              </Link>
              {property.data?.property.geocodeStatus === 'Geocoded' &&
              toNumericCoordinate(property.data.property.latitude) != null &&
              toNumericCoordinate(property.data.property.longitude) != null ? (
                <PropertyLocationMap
                  latitude={toNumericCoordinate(property.data.property.latitude)!}
                  longitude={toNumericCoordinate(property.data.property.longitude)!}
                  editable={false}
                  compact
                />
              ) : null}
              {/* Owner/manager, read-only (M1-25) — estimate/invoice screens
                  don't exist yet (M2), so this is the one place today the AC's
                  "where the office needs them" actually applies. */}
              {propertyParty(property.data?.property.parties, 'Owner') ? (
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {t('jobDetail.propertyOwner', {
                    name: propertyParty(property.data?.property.parties, 'Owner'),
                  })}
                </p>
              ) : null}
              {propertyParty(property.data?.property.parties, 'PropertyManager') ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {t('jobDetail.propertyManager', {
                    name: propertyParty(property.data?.property.parties, 'PropertyManager'),
                  })}
                </p>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobDetail.jobType')}
            </dt>
            <dd>{record.jobType ?? t('jobDetail.noJobType')}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobDetail.priority')}
            </dt>
            <dd>{t(`common.jobPriority.${record.priority}`, { defaultValue: record.priority })}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobDetail.scheduled')}
            </dt>
            <dd>
              {record.scheduledStart && appointmentZone
                ? formatInstantWithZone(record.scheduledStart, appointmentZone)
                : t('jobDetail.notScheduled')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobDetail.technician')}
            </dt>
            <dd data-testid="job-detail-technician">
              {record.assignedUserName ?? t('jobDetail.unassigned')}
            </dd>
          </div>
        </dl>
      </div>

      {/* ---- Visits (M2-52) ------------------------------------------------ */}
      {appointmentZone ? (
        <JobVisits
          job={record}
          timeZone={appointmentZone}
          canEdit={canAssign}
          technicians={technicians.data ?? []}
          onAnnounce={setAnnouncement}
          // Rule 5c, held here too: the API sends anyone but the office
          // null, and a server that leaked the list still would not reach
          // the screen (M2-16's reader test, again).
          appointmentEmails={canManageCustomers ? detail.appointmentEmails : null}
          onMyWay={detail.onMyWay}
          currentUserId={currentUser.data?.userId}
        />
      ) : null}

      {/* ---- Time (M2-33): the technician's timer and the job's time -------- */}
      {timeZone && jobId ? (
        <JobTime
          jobId={jobId}
          job={record}
          timeZone={timeZone}
          canManage={canManageCustomers}
          technicians={technicians.data ?? []}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {/* ---- How this job came in ------------------------------------------ */}
      {/*
          M1-45. Its own attribution, deliberately separate from the customer's:
          a repeat customer who called this time from a different ad is exactly
          the case the field exists to record. `POST /jobs` has accepted one
          since M1-28 and NewJobDialog now sends it, defaulted from the customer.
      */}
      <div className="rounded-lg border p-4">
        <h2 className="text-base font-semibold">{t('jobDetail.leadSourceHeading')}</h2>
        <div className="mt-3">
          <LeadSourceField
            leadSourceId={record.leadSourceId}
            leadSourceLabel={record.leadSourceLabel}
            leadSourceDetail={record.leadSourceDetail}
            canManage={canManageCustomers}
            onSave={(leadSourceId, leadSourceDetail) =>
              setLeadSource.mutateAsync({ leadSourceId, leadSourceDetail })
            }
            onSaved={(label) =>
              setAnnouncement(
                label
                  ? t('jobDetail.leadSourceSaved', { label })
                  : t('jobDetail.leadSourceCleared'),
              )
            }
          />
        </div>
      </div>

      {/* ---- Status ------------------------------------------------------ */}
      {detail.allowedTransitions.length > 0 ? (
        <div className="space-y-3 rounded-lg border p-4">
          <h2 className="text-base font-semibold">{t('jobDetail.status.heading')}</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">
                {t('jobDetail.status.moveTo')}
              </span>
              <select
                className={fieldClass}
                value={targetStatus}
                onChange={(event) => setTargetStatus(event.target.value)}
              >
                <option value="">{t('jobDetail.status.choose')}</option>
                {detail.allowedTransitions.map((status) => (
                  <option key={status} value={status}>
                    {t(`common.jobStatus.${status}`, { defaultValue: status })}
                  </option>
                ))}
              </select>
            </label>

            {targetStatus === 'Scheduled' ? (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-[var(--color-muted-foreground)]">
                    {t('jobDetail.status.date')}
                  </span>
                  <input
                    type="date"
                    className={fieldClass}
                    value={scheduleDate}
                    onChange={(event) => setScheduleDate(event.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-[var(--color-muted-foreground)]">
                    {t('jobDetail.status.startTime')}
                  </span>
                  <input
                    type="time"
                    className={fieldClass}
                    value={scheduleStart}
                    aria-describedby={appointmentZone ? 'job-schedule-zone' : undefined}
                    onChange={(event) => setScheduleStart(event.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-[var(--color-muted-foreground)]">
                    {t('jobDetail.status.endTime')}
                  </span>
                  <input
                    type="time"
                    className={fieldClass}
                    value={scheduleEnd}
                    aria-describedby={appointmentZone ? 'job-schedule-zone' : undefined}
                    onChange={(event) => setScheduleEnd(event.target.value)}
                  />
                </label>
                {appointmentZone ? (
                  <p
                    id="job-schedule-zone"
                    className="w-full text-xs text-[var(--color-muted-foreground)]"
                    data-testid="schedule-zone"
                  >
                    {t('jobDetail.appointmentClock', {
                      // Named for the day being booked (summer time or not), else today.
                      zone: zoneName(
                        (() => {
                          const day = parseIsoDate(scheduleDate);
                          return day ? zonedTimeToInstant(day, 12, 0, appointmentZone) : new Date();
                        })(),
                        appointmentZone,
                      ),
                      id: appointmentZone,
                    })}
                  </p>
                ) : null}
              </>
            ) : null}

            <Button
              type="button"
              size="sm"
              disabled={!targetStatus || changeStatus.isPending}
              onClick={submitStatusChange}
            >
              {changeStatus.isPending
                ? t('jobDetail.status.changing')
                : t('jobDetail.status.confirm')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* ---- Invoice (M2-10) ------------------------------------------------ */}
      {jobId ? (
        <JobInvoice
          jobId={jobId}
          status={record.status}
          customerName={record.customerName}
          lineCount={detail.lines.length}
          invoices={detail.invoices}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {/* ---- Assignment ---------------------------------------------------- */}
      {canAssign ? (
        <div className="space-y-3 rounded-lg border p-4">
          <h2 className="text-base font-semibold">{t('jobDetail.assign.heading')}</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">
                {t('jobDetail.assign.technician')}
              </span>
              <select
                className={fieldClass}
                value={assignTo}
                onChange={(event) => setAssignTo(event.target.value)}
              >
                <option value="">{t('jobDetail.assign.unassignedOption')}</option>
                {(technicians.data ?? []).map((technician) => (
                  <option key={technician.id} value={technician.id}>
                    {technician.name}
                  </option>
                ))}
              </select>
            </label>
            <Button type="button" size="sm" disabled={assign.isPending} onClick={submitAssign}>
              {assign.isPending ? t('jobDetail.assign.assigning') : t('jobDetail.assign.confirm')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* ---- Photos --------------------------------------------------------- */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold">{t('jobDetail.photos.heading')}</h2>

        {photos.isPending ? (
          <LoadingState label={t('jobDetail.photos.loading')} />
        ) : photos.isError ? (
          <ErrorState
            title={t('jobDetail.photos.loadErrorTitle')}
            onRetry={() => void photos.refetch()}
          />
        ) : (photos.data ?? []).length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('jobDetail.photos.empty')}
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(photos.data ?? []).map((photo) => {
              const href = photo.thumbnailUrl ?? photo.originalUrl;
              return (
                <li key={photo.id}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('jobDetail.photos.viewFull', {
                        author: photo.uploadedByName,
                        date: formatDate(photo.createdAt, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                          timeZone,
                        }),
                      })}
                      className="block aspect-square overflow-hidden rounded-md border focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                    >
                      <img src={href} alt="" className="h-full w-full object-cover" />
                    </a>
                  ) : (
                    <div className="flex aspect-square items-center justify-center rounded-md border text-xs text-[var(--color-muted-foreground)]">
                      {t(`jobDetail.photos.status.${photo.status}`, { defaultValue: photo.status })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Announced: an upload started from this device, and its outcome
            matters even to someone who cannot see the thumbnail appear. */}
        {upload.uploads.length > 0 ? (
          <ul aria-live="polite" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {upload.uploads.map((item) => (
              <UploadItemCard
                key={item.localId}
                item={item}
                onRetry={upload.retry}
                onDismiss={upload.dismiss}
                t={t}
              />
            ))}
          </ul>
        ) : null}

        <label className="inline-flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('jobDetail.photos.addLabel')}</span>
          <input
            type="file"
            accept={upload.acceptedTypes.join(',')}
            multiple
            className="text-sm"
            onChange={(event) => {
              if (event.target.files) {
                upload.addFiles(event.target.files);
              }
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {/* ---- Work from approved estimates (M2-09) --------------------------- */}
      <JobWork lines={detail.lines} sourceEstimates={detail.sourceEstimates} timeZone={timeZone} />

      {/* ---- Estimates (M2-06) ----------------------------------------------- */}
      {jobId ? (
        <JobEstimates
          jobId={jobId}
          customerId={detail.job.customerId}
          propertyId={detail.job.propertyId}
        />
      ) : null}

      {/* ---- Forms (M1-20) --------------------------------------------------- */}
      {jobId ? <JobForms jobId={jobId} canAttach={canAssign} /> : null}

      {/* ---- Notes ---------------------------------------------------------- */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold">{t('jobDetail.notes.heading')}</h2>

        {detail.notes.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('jobDetail.notes.empty')}
          </p>
        ) : (
          <ul className="space-y-3">
            {detail.notes.map((note) => (
              <li key={note.id} className="rounded-lg border p-3 text-sm" data-testid="job-note">
                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <label className="block">
                      <span className="sr-only">{t('jobDetail.notes.body')}</span>
                      <textarea
                        className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                        rows={3}
                        value={editBody}
                        onChange={(event) => setEditBody(event.target.value)}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={editCustomerVisible}
                        onChange={(event) => setEditCustomerVisible(event.target.checked)}
                      />
                      {t('jobDetail.notes.customerVisible')}
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={editNote.isPending}
                        onClick={() => submitNoteEdit(note.id)}
                      >
                        {t('jobDetail.notes.saveEdit')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingNoteId(null)}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="whitespace-pre-line">{note.body}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                      <span>
                        {t('jobDetail.notes.byline', {
                          author: note.authorName,
                          date: formatDate(note.createdAt, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                            timeZone,
                          }),
                        })}
                      </span>
                      {note.wasEdited ? <span>{t('jobDetail.notes.editedTag')}</span> : null}
                      {note.customerVisible ? (
                        <span className="rounded-full border px-1.5 py-0.5">
                          {t('jobDetail.notes.customerVisibleTag')}
                        </span>
                      ) : null}
                      {currentUser.data?.userId === note.authorUserId ? (
                        <button
                          type="button"
                          className="underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                          onClick={() => startEditingNote(note)}
                        >
                          {t('jobDetail.notes.edit')}
                        </button>
                      ) : null}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2 rounded-lg border p-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t('jobDetail.notes.addLabel')}</span>
            <textarea
              className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
              rows={3}
              value={noteBody}
              onChange={(event) => setNoteBody(event.target.value)}
              placeholder={t('jobDetail.notes.addPlaceholder')}
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={noteCustomerVisible}
              onChange={(event) => setNoteCustomerVisible(event.target.checked)}
            />
            {t('jobDetail.notes.customerVisible')}
          </label>
          <Button
            type="button"
            size="sm"
            disabled={!noteBody.trim() || addNote.isPending}
            onClick={submitNote}
          >
            {addNote.isPending ? t('jobDetail.notes.adding') : t('jobDetail.notes.add')}
          </Button>
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only" data-testid="job-detail-announcement">
        {announcement}
      </p>
    </section>
  );
}

function UploadItemCard({
  item,
  onRetry,
  onDismiss,
  t,
}: {
  item: UploadItem;
  onRetry: (localId: string) => void;
  onDismiss: (localId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <li data-testid="photo-upload-item">
      <div className="flex aspect-square items-center justify-center rounded-md border text-xs text-[var(--color-muted-foreground)]">
        {t(`jobDetail.photos.uploadStatus.${item.status}`)}
      </div>
      <span className="mt-1 block truncate text-xs">{item.fileName}</span>
      {item.status === 'failed' ? (
        <div className="mt-1 flex gap-2 text-xs">
          <button
            type="button"
            className="underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
            onClick={() => onRetry(item.localId)}
          >
            {t('jobDetail.photos.retry')}
          </button>
          <button
            type="button"
            className="underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
            onClick={() => onDismiss(item.localId)}
          >
            {t('common.dismiss')}
          </button>
        </div>
      ) : null}
    </li>
  );
}
